import React from 'react';

/**
 * Decodes HTML entities if the content contains escaped HTML tags.
 * Handles &lt;p&gt; -> <p>, &amp; -> &, &quot; -> ", etc.
 */
export const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return '';
  // Check if string contains escaped tags like &lt;p&gt; or &lt;div
  if (/&lt;\/?[a-z][\s\S]*?&gt;/i.test(str) || /&(?:lt|gt|amp|quot|#39);/.test(str)) {
    return str
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }
  return str;
};

/**
 * Cleans and normalizes HTML for consistent rendering across browsers.
 */
export const processHtmlContent = (htmlContent) => {
  if (!htmlContent) return '';
  if (typeof htmlContent !== 'string') return String(htmlContent);

  let cleaned = decodeHtmlEntities(htmlContent);

  return cleaned
    .replace(/<center>/gi, '<div style="text-align: center;">')
    .replace(/<\/center>/gi, '</div>')
    .replace(/<div[^>]*align="left"[^>]*>/gi, '<div style="text-align: left;">')
    .replace(/<div[^>]*align="right"[^>]*>/gi, '<div style="text-align: right;">')
    .replace(/<div[^>]*align="justify"[^>]*>/gi, '<div style="text-align: justify;">')
    .replace(/<p[^>]*align="center"[^>]*>/gi, '<p style="text-align: center;">')
    .replace(/<p[^>]*align="left"[^>]*>/gi, '<p style="text-align: left;">')
    .replace(/<p[^>]*align="right"[^>]*>/gi, '<p style="text-align: right;">')
    .replace(/<p[^>]*align="justify"[^>]*>/gi, '<p style="text-align: justify;">');
};

/**
 * Checks whether a string contains HTML tags
 */
export const containsHtml = (str) => {
  if (!str || typeof str !== 'string') return false;
  return /<[a-z][\s\S]*>/i.test(decodeHtmlEntities(str));
};

/**
 * Safe HTML renderer component that resolves HTML content,
 * handles escaped entities, applies responsive academic styling,
 * and gracefully falls back for plain text or missing content.
 */
const HtmlContent = ({
  content,
  fallback = null,
  className = '',
  as = 'div',
  inline = false,
  style = {}
}) => {
  const raw = content !== undefined && content !== null && content !== '' ? content : fallback;

  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  // If not string (e.g. number or JSX), render safely
  if (typeof raw !== 'string') {
    if (React.isValidElement(raw)) {
      const Tag = as || (inline ? 'span' : 'div');
      return <Tag className={className} style={style}>{raw}</Tag>;
    }
    if (typeof raw === 'number') {
      const Tag = as || (inline ? 'span' : 'div');
      return <Tag className={className} style={style}>{raw}</Tag>;
    }
    if (raw && typeof raw === 'object') {
      const sec = raw._seconds || raw.seconds;
      if (sec) {
        const Tag = as || (inline ? 'span' : 'div');
        return <Tag className={className} style={style}>{new Date(sec * 1000).toLocaleDateString()}</Tag>;
      }
      if (raw instanceof Date) {
        const Tag = as || (inline ? 'span' : 'div');
        return <Tag className={className} style={style}>{raw.toLocaleDateString()}</Tag>;
      }
      if (typeof raw.toDate === 'function') {
        const Tag = as || (inline ? 'span' : 'div');
        return <Tag className={className} style={style}>{raw.toDate().toLocaleDateString()}</Tag>;
      }
      return null;
    }
    return null;
  }

  const processed = processHtmlContent(raw);
  const hasHtml = /<[a-z][\s\S]*>/i.test(processed);

  const Tag = as || (inline ? 'span' : 'div');

  if (hasHtml) {
    return (
      <Tag
        className={`rich-page-content ${className}`.trim()}
        style={style}
        dangerouslySetInnerHTML={{ __html: processed }}
      />
    );
  }

  // Plain text: if it has newlines, preserve line breaks
  if (processed.includes('\n')) {
    return (
      <Tag className={`whitespace-pre-line ${className}`.trim()} style={style}>
        {processed}
      </Tag>
    );
  }

  return (
    <Tag className={className} style={style}>
      {processed}
    </Tag>
  );
};

export default HtmlContent;
